import { useState } from 'react';
import { useAccountApi } from '../../api/AccountApiProvider';
import { toApiError } from '../../api/errors';
import type { ApiError } from '../../api/errors';
import type { Rollout, RolloutBoard, TenantVersions } from '../../api/types';
import { useI18n } from '../../i18n/useI18n';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { AttributionNotice } from '../../ui/AttributionNotice';
import { Button, Chip } from '../../ui/primitives';
import { Modal } from '../../ui/Modal';
import { advanceOffer, waveLabelKey, waveMembers, waveProgress } from './rollout';

/**
 * Signing a wave off, which is what lets the rest of the fleet move.
 *
 * The dialog shows both halves of that decision: what has already moved in this
 * wave — and what did not — and which tenancies the next wave will move, in the
 * order they will move. Somebody signing off is deciding for the tenancies in
 * the second list on the strength of the first.
 *
 * It also says who is deciding. The control plane lists **findings** — a tenancy
 * it could not move, one whose own diagnosis does not accept it, a pair of
 * components with a named break between them — and refuses a sign-off while any
 * of them stands. It cannot see the thing a canary is kept for: a customer
 * ringing up to say their sessions drop. So no findings lifts the veto and
 * decides nothing, and the machine never advances a rollout on its own.
 */
export function SignOffWaveDialog({
  board,
  rollout,
  actorName,
  onClose,
  onSigned,
}: {
  board: RolloutBoard;
  rollout: Rollout;
  actorName: string;
  onClose: () => void;
  onSigned: (board: RolloutBoard) => void;
}) {
  const { t } = useI18n();
  const api = useAccountApi();

  const [failure, setFailure] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  const offer = advanceOffer(rollout);
  const progress = waveProgress(board, rollout.wave);
  const next = offer.offered && !offer.finishes ? waveMembers(board, offer.next) : [];

  async function sign(): Promise<void> {
    setFailure(null);
    setBusy(true);
    try {
      onSigned(await api.provider.advanceRollout());
    } catch (error: unknown) {
      setFailure(toApiError(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t('rolloutSignOff.title')}
      subtitle={t('rolloutSignOff.subtitle', {
        wave: t(waveLabelKey(rollout.wave)),
        target: rollout.target,
      })}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <span className="spacer" />
          <Button
            variant="primary"
            disabled={busy || !offer.offered}
            onClick={() => void sign()}
          >
            {busy ? `${t('rolloutSignOff.submitting')}…` : t('rolloutSignOff.submit')}
          </Button>
        </>
      }
    >
      <div className="stack">
        {failure !== null && <SignOffFailure failure={failure} />}

        {!offer.offered && (
          // The board already carries this sentence; it is repeated here because
          // this is the screen where somebody was about to press the button.
          <Alert tone="warn" title={t('rollout.advance.blocked.title')} live>
            {offer.blockedBy === undefined ? (
              <p>{t('rollout.advance.blocked.noReason')}</p>
            ) : (
              <p className="mono">{offer.blockedBy}</p>
            )}
          </Alert>
        )}

        <Alert tone="neutral" title={t('rolloutSignOff.already.title')}>
          <Group slugs={progress.moved} labelKey="rolloutSignOff.already.moved" />
          {/* Its own line, because it is its own fact: a wave signed off with a
              repair in it must not read like one that never needed a repair. */}
          <Group slugs={progress.repaired} labelKey="rolloutSignOff.already.repaired" />
          <Group slugs={progress.failed} labelKey="rolloutSignOff.already.failed" />
          <Group slugs={progress.pending} labelKey="rolloutSignOff.already.pending" />
        </Alert>

        {offer.offered && offer.finishes ? (
          <Alert tone="info" title={t('rolloutSignOff.finishes.title')}>
            <p>{t('rolloutSignOff.finishes.body')}</p>
          </Alert>
        ) : (
          offer.offered && (
            <Alert
              tone="info"
              title={t('rolloutSignOff.next.title', { wave: t(waveLabelKey(offer.next)) })}
            >
              {next.length === 0 ? (
                <p>{t('rolloutSignOff.next.none')}</p>
              ) : (
                <>
                  <p>{t('rolloutSignOff.next.order', { target: rollout.target })}</p>
                  <div className="row" style={{ marginTop: 8 }}>
                    {next.map((row) => (
                      <Chip key={row.slug}>{row.slug}</Chip>
                    ))}
                  </div>
                </>
              )}
            </Alert>
          )
        )}

        <Alert tone="warn" title={t('rolloutSignOff.judgement.title')}>
          <p>{t('rolloutSignOff.judgement.body')}</p>
          <p style={{ marginTop: 8 }}>{t('rolloutSignOff.judgement.soak')}</p>
        </Alert>

        <AttributionNotice actorName={actorName} />
      </div>
    </Modal>
  );
}

/** One group of tenancies, named. An empty group says it is empty, in words. */
function Group({
  slugs,
  labelKey,
}: {
  slugs: TenantVersions[];
  labelKey:
    | 'rolloutSignOff.already.moved'
    | 'rolloutSignOff.already.repaired'
    | 'rolloutSignOff.already.failed'
    | 'rolloutSignOff.already.pending';
}) {
  const { t } = useI18n();

  return (
    <div style={{ marginTop: 8 }}>
      <p>{t(labelKey)}</p>
      {slugs.length === 0 ? (
        <p className="faint">{t('rolloutSignOff.already.nobody')}</p>
      ) : (
        <div className="row" style={{ marginTop: 6 }}>
          {slugs.map((row) => (
            <Chip key={row.slug}>{row.slug}</Chip>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A refusal of the sign-off itself.
 *
 * `conflict` is the server saying which of the five reasons it is — still
 * moving, stopped, over, something wrong with the wave, or not yet stood long
 * enough — and when it is the fourth, its sentence names the tenancies. That
 * sentence is shown as it stands.
 */
function SignOffFailure({ failure }: { failure: ApiError }) {
  const { t } = useI18n();

  if (failure.code === 'conflict') {
    return (
      <Alert tone="deny" title={t('rolloutSignOff.refused.title')} live>
        <p className="mono">{failure.message}</p>
        <p style={{ marginTop: 8 }}>{t('rolloutSignOff.refused.body')}</p>
      </Alert>
    );
  }
  return <ErrorAlert error={failure} />;
}
